/* eslint-disable import/no-extraneous-dependencies */
import { type Ref, useCallback, useEffect, useRef, useState } from 'react';

import type { Types } from '@cornerstonejs/core';
import {
  cache,
  getEnabledElement,
  getRenderingEngine,
  getShouldUseCPURendering,
  isCornerstoneInitialized,
  metaData,
} from '@cornerstonejs/core';
import type { Point2 } from '@cornerstonejs/core/dist/esm/types';
import {
  ArrowAnnotateTool,
  CircleROITool,
  EllipticalROITool,
  LengthTool,
  MagnifyTool,
  PanTool,
  PlanarFreehandROITool,
  ProbeTool,
  RectangleROITool,
  ToolGroupManager,
  WindowLevelTool,
  ZoomToolMouseWheelTool,
  annotation,
  segmentation,
} from '@cornerstonejs/tools';
import { Events, MouseBindings } from '@cornerstonejs/tools/dist/esm/enums';
import { triggerAnnotationRenderForViewportIds } from '@cornerstonejs/tools/dist/esm/utilities';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import type { RootStateOrAny } from 'react-redux';
import { useSelector } from 'react-redux';

import {
  AREA_OF_CONCERN,
  CALCULUS,
  CARIES,
  DEFAULT_BRIGHTNESS_VALUE,
  DEFAULT_CONTRAST_VALUE,
  IMAGES_WITH_METADATA,
  PERIAPICAL_RADIOLUCENCY,
  RADIOGRAPHIC_BONELOSS,
  RESTORATIVE_ADJACENT_LUCENCY,
} from '../../constants';
import {
  determineClassForFinding,
  determineClassForSegmentation,
  determineColorForFinding,
  isWebImageType,
} from '../../modules/annotator/annotation-utils/annotation-utils';
import { isWithinSensitivityRange } from '../../modules/annotator/annotation-utils/incipient';
import AnnotatorConstants from '../../modules/annotator/annotator.constants';
import {
  shouldDisplayRootTipsAtom,
  viewerModeAtom,
} from '../../modules/annotator/atoms';
import {
  annotateActionAtom,
  deleteUserAnnotationAtom,
  hiddenAnnotationsAtom,
  selectedFindingImageIdAtom,
  selectedFindingsAtom,
} from '../../modules/annotator/casePresentation/atoms';
import { isMatchingRBLPoint } from '../../modules/annotator/casePresentation/helpers';
import { cornerstoneLog } from '../../modules/annotator/debug-logger';
import {
  modelsTogglesAtom,
  toothPartsTogglesAtom,
} from '../../modules/annotator/findingSidebar/atoms';
import {
  binFilterAtom,
  confidenceScoreFilterAtom,
} from '../../modules/annotator/findingSidebar/confidenceScoreFilter/atoms';
import { useIsWithinConfidenceThreshold } from '../../modules/annotator/findingSidebar/confidenceScoreFilter/helpers';
import { imageBrowserExpandedAtom } from '../../modules/annotator/multiFileViewer/findingViewer/imageBrowser/atoms';
import { computeVisibleFindings } from '../../modules/annotator/multiFileViewer/findingViewer/utils';
import type { HDVersionType } from '../../modules/annotator/multiFileViewer/imageToolbar/atoms';
import {
  brightnessValueAtom,
  contrastValueAtom,
  enabledHDAtom,
  hideFindingsAtom,
  imageQualityAssessmentOverlayOpenAtom,
  isOpacityInvertedAtom,
  shouldPersistUserModificationsAtom,
  showSegmentationAtom,
} from '../../modules/annotator/multiFileViewer/imageToolbar/atoms';
import { useRotation } from '../../modules/annotator/multiFileViewer/RotationContext';
import type { ImageObject } from '../../modules/annotator/types';
import { getCariesSubTypedFindings } from '../../modules/annotator/utils';
import { selectedAnalysesAtom } from '../../modules/patientList/atoms';
import { usePreferences } from '../../react-query/usePreferences';
import { useGetImageWithSegmentations } from '../../react-query/useSegmentations';
import { useIncipient } from '../../routes/IncipientContext';
import { computeFileExtension } from '../file';
import { useTrackImageView } from '../mixpanel';
import {
  determineRBLLineClass,
  determineRBLLineColor,
  getAbdCejAndRootPoints,
  isModerateOrSevereRblFinding,
} from '../rbl';
import { PracticeFeatureFlagKey, useFeatureFlag } from '../user';

import { renderingEngineAtom } from './cornerstoneAtoms';
import {
  computePointsArrayFromBorderedFinding,
  isEllipseFinding,
} from './findings';
import hardcodedMetaDataProvider from './hardcodedMetaDataProvider';
import {
  buildCornerstoneFriendlyAnnotation,
  useTrackImageLoadTime,
} from './helpers';
import {
  RENDERING_ENGINE,
  initializeCornerstoneElement,
  initializeToolsForImage,
  thumbnailToolGroupId,
  toolGroupId,
} from './initializeCornerstone';
import type { Source } from './types';

type MakeOptionalExcept<T, K extends keyof T> = Partial<T> &
  Pick<Required<T>, K>;
export type CornerstoneImage = MakeOptionalExcept<ImageObject, 'imageId'>;

export const getSelectedImagePath = (
  activeImage: CornerstoneImage,
  preferDisplayOverSharpened: boolean,
  dentalPhotoThumbnail?: string,
  disableAdjustmentForPanos?: boolean,
  hdVersion?: HDVersionType,
) => {
  if (!activeImage) {
    return '';
  }

  if (activeImage.detectedImageType === 'COLOR_IMAGE') {
    if (dentalPhotoThumbnail) {
      return dentalPhotoThumbnail;
    }
    return activeImage.originalPath;
  }

  if (
    disableAdjustmentForPanos &&
    activeImage.detectedImageType === 'PANORAMIC'
  ) {
    return activeImage.path || activeImage.originalPath || '';
  }

  if (hdVersion === 'HD2' && activeImage.sharpenedHD2Path) {
    return activeImage.sharpenedHD2Path;
  } else if (!!hdVersion && activeImage.sharpenedPath) {
    return activeImage.sharpenedPath;
  } else if (
    preferDisplayOverSharpened &&
    isWebImageType(activeImage.originalPath)
  ) {
    return activeImage.originalPath;
  } else if (activeImage.path) {
    return activeImage.path;
  } else if (isWebImageType(activeImage.originalPath)) {
    return activeImage.originalPath;
  } else {
    return activeImage.sharpenedPath || '';
  }
};
/**
 * This hook handles the initialization of cornerstone + cornerstoneTools, as well as abstracting away
 * the draw findings function.
 */
export const useCornerstone = ({
  activeImage,
  isThumbnail,
  dicomCanvasRef,
  viewportId,
  source,
  side,
  selectedTeeth,
  visitId,
}: {
  activeImage: CornerstoneImage;
  isThumbnail: boolean;
  dicomCanvasRef: Ref<HTMLDivElement> | HTMLDivElement | null;
  viewportId: string;
  source: Source;
  side?: 'left' | 'right';
  selectedTeeth?: string[];
  visitId?: string;
}) => {
  const [imageLoadStatus, setImageLoadStatus] = useState<
    'loading' | 'error' | 'success'
  >('loading');
  const enabledHD = useAtomValue(enabledHDAtom);
  const [isOpacityInverted] = useAtom(isOpacityInvertedAtom);
  const { mirroredImages } = useRotation();
  const [brightnessValue, setBrightnessValue] = useAtom(brightnessValueAtom);
  const [contrastValue, setContrastValue] = useAtom(contrastValueAtom);
  const [viewerMode] = useAtom(viewerModeAtom);
  const [hideFindings] = useAtom(hideFindingsAtom);
  const { findingType, patientId: thumbnailPreviewsPatientId } =
    useAtomValue(selectedAnalysesAtom);
  const isRenderingEngineInitialized = useAtomValue(renderingEngineAtom);
  const renderingEngine =
    isRenderingEngineInitialized && getRenderingEngine(RENDERING_ENGINE);
  const modelsToggles = useAtomValue(modelsTogglesAtom);
  const toothPartsToggles = useAtomValue(toothPartsTogglesAtom);
  const showSegmentations =
    useAtomValue(showSegmentationAtom) &&
    activeImage?.detectedImageType !== 'COLOR_IMAGE';
  const setShouldPersistUserModifications = useSetAtom(
    shouldPersistUserModificationsAtom,
  );
  const confidenceScoreFilter = useAtomValue(confidenceScoreFilterAtom);
  const binFilterValue = useAtomValue(binFilterAtom);
  const annotateAction = useAtomValue(annotateActionAtom);
  const hiddenAnnotations = useAtomValue(hiddenAnnotationsAtom);
  const isImageBrowserExpanded = useAtomValue(imageBrowserExpandedAtom);
  const shouldDisplayRootTips = useAtomValue(shouldDisplayRootTipsAtom);
  const isImageQualityAssessmentOpen = useAtomValue(
    imageQualityAssessmentOverlayOpenAtom,
  );

  const imageWithSegmentations = useGetImageWithSegmentations(
    activeImage?.imageId,
    visitId,
  );

  const practiceThreshold = useSelector(
    (state: RootStateOrAny) =>
      state.login.user.practice?.rblSettings?.templateName,
  );

  const { rotatedImages } = useRotation();

  const { trackImageView } = useTrackImageView();
  const { timerEnd, timerStart } = useTrackImageLoadTime();

  const selectedFinding = useAtomValue(selectedFindingsAtom);
  const selectedFindingImageId = useAtomValue(selectedFindingImageIdAtom);
  const shouldDeleteUserAnnotation = useAtomValue(deleteUserAnnotationAtom);
  const showToothSpecificFindings = !selectedFinding && selectedTeeth?.length;
  const groupId = isThumbnail ? thumbnailToolGroupId : toolGroupId;

  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();

  // Helper function to determine if selection filtering should be applied
  const shouldApplySelectionFiltering = () => {
    if (!selectedFinding) return false;

    return (
      selectedFindingImageId === activeImage.imageId &&
      source !== 'compare-view'
    );
  };

  useEffect(() => {
    cornerstoneLog.log('effect: initialize cornerstone element', {
      imageId: activeImage?.imageId,
      viewportId,
      source,
      isThumbnail,
      isCornerstoneInit: isCornerstoneInitialized(),
      hasRenderingEngine: !!renderingEngine,
    });
    if (
      !isCornerstoneInitialized() ||
      !renderingEngine ||
      !activeImage?.imageId
    ) {
      return;
    }

    initializeCornerstoneElement(
      dicomCanvasRef as HTMLDivElement,
      viewportId,
      renderingEngine,
    );
    initializeToolsForImage(viewportId, groupId);

    return () => {
      cornerstoneLog.log('cleanup: disable cornerstone element', {
        viewportId,
      });
      const viewportForImage = renderingEngine.getViewport(viewportId);
      if (viewportForImage && source !== 'case-presentation-download') {
        annotation.state
          .getAnnotationManager()
          .removeAnnotations(viewportForImage.getCurrentImageId());
        try {
          viewportForImage.getImageData();
          renderingEngine.disableElement(viewportId);
        } catch {
          /**
             This avoids a white screen when In CPU rendering in which we sometimes
            encounter a state where we attempt to disable an element that has already been cleared
           */
        }
      }
    };
  }, [
    activeImage?.imageId,
    isThumbnail,
    dicomCanvasRef,
    renderingEngine,
    viewportId,
    source,
    groupId,
  ]);

  const isCornerstoneReady = !!(
    renderingEngine &&
    activeImage &&
    dicomCanvasRef &&
    ToolGroupManager.getToolGroup(groupId)
  );

  // This useEffect syncs our toolbar state with cornerstone mouse events, necessary for resetting toolbar state
  useEffect(() => {
    const handler = () => {
      const viewportForImage =
        renderingEngine &&
        (renderingEngine.getViewport(viewportId) as
          | Types.IStackViewport
          | undefined);
      if (!viewportForImage) {
        return;
      }
      const voiRange = viewportForImage.getProperties().voiRange;
      setBrightnessValue(
        Math.round(voiRange?.upper || DEFAULT_BRIGHTNESS_VALUE),
      );
      setContrastValue(Math.round(voiRange?.lower || DEFAULT_CONTRAST_VALUE));
      setShouldPersistUserModifications(true);
    };
    if (dicomCanvasRef && !isThumbnail) {
      // @ts-expect-error TS(2339): Property 'addEventListener' does not exist on type... Remove this comment to see the full error message
      dicomCanvasRef.addEventListener(Events.MOUSE_DRAG, handler);
      return () => {
        // @ts-expect-error TS(2339): Property 'removeEventListener' does not exist on t... Remove this comment to see the full error message
        dicomCanvasRef.removeEventListener(Events.MOUSE_DRAG, handler);
      };
    }
  }, [
    dicomCanvasRef,
    isThumbnail,
    setBrightnessValue,
    setContrastValue,
    renderingEngine,
    viewportId,
    setShouldPersistUserModifications,
  ]);

  const setViewportProperties = (viewportForImage: Types.IStackViewport) => {
    let newViewportProperties: Types.StackViewportProperties = {
      rotation: rotatedImages?.[activeImage?.imageId] ?? 0,
    };
    // Only GPU rendering should set invert/brightness/contrast for now
    if (!getShouldUseCPURendering() && !isThumbnail) {
      newViewportProperties = {
        ...newViewportProperties,
        invert: isOpacityInverted,
        voiRange: {
          upper: brightnessValue,
          lower: contrastValue,
        },
      };
    }

    let isHorizontallyFlipped: boolean | undefined = false;
    try {
      // Apply mirroring using the camera's flip properties
      isHorizontallyFlipped = viewportForImage.getCamera().flipHorizontal;
    } catch (error) {
      // Catch for CPU engine error
    }

    // Get per-image mirror state
    const isImageMirrored =
      activeImage?.imageId && mirroredImages
        ? mirroredImages[activeImage.imageId] || false
        : false;
    // Only update if the flip states have changed
    if (isHorizontallyFlipped !== isImageMirrored) {
      viewportForImage.setCamera({
        flipHorizontal: isImageMirrored,
      });
    }
    viewportForImage?.setProperties(newViewportProperties);
  };

  // update cornerstone viewport if toolbar settings change
  useEffect(() => {
    cornerstoneLog.log('effect: viewport properties update', {
      viewportId,
      source,
      imageLoadStatus,
      brightnessValue,
      contrastValue,
      isOpacityInverted,
      isThumbnail,
    });
    const viewportForImage =
      renderingEngine &&
      (renderingEngine.getViewport(viewportId) as
        | Types.IStackViewport
        | undefined);
    if (
      source === 'additional-image' ||
      !dicomCanvasRef ||
      !viewportForImage ||
      !renderingEngine ||
      !getEnabledElement(dicomCanvasRef as HTMLDivElement) ||
      !activeImage ||
      !viewportForImage.getCurrentImageId() ||
      imageLoadStatus !== 'success'
    ) {
      return;
    }
    setViewportProperties(viewportForImage);
    viewportForImage.render();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'dicomCanvasRef' and 'source'. Either include them or remove the dependency array. */
  }, [
    brightnessValue,
    contrastValue,
    rotatedImages,
    activeImage,
    renderingEngine,
    viewportId,
    imageLoadStatus,
    isThumbnail,
    isOpacityInverted,
    mirroredImages,
    isCornerstoneReady,
  ]);

  /**
   * This is called upon loading the image successfully.
   * We check isThumbnail because we only want to adjust the contrast for the selected image
   */
  const changeViewerMode = useCallback(
    (mode: number) => {
      const toolGroup = ToolGroupManager.getToolGroup(groupId);
      if (annotateAction && !isThumbnail) {
        if (annotateAction === 'arrow') {
          toolGroup?.setToolEnabled(PlanarFreehandROITool.toolName);
          toolGroup?.setToolEnabled(CircleROITool.toolName);
          toolGroup?.setToolActive(ArrowAnnotateTool.toolName, {
            bindings: [
              {
                mouseButton: MouseBindings.Primary, // Left Click
              },
            ],
          });
        } else if (annotateAction === 'circle') {
          toolGroup?.setToolEnabled(PlanarFreehandROITool.toolName);
          toolGroup?.setToolEnabled(ArrowAnnotateTool.toolName);
          toolGroup?.setToolActive(CircleROITool.toolName, {
            bindings: [
              {
                mouseButton: MouseBindings.Primary, // Left Click
              },
            ],
          });
        } else if (annotateAction === 'segment') {
          toolGroup?.setToolEnabled(CircleROITool.toolName);
          toolGroup?.setToolEnabled(ArrowAnnotateTool.toolName);
          toolGroup?.setToolActive(PlanarFreehandROITool.toolName, {
            bindings: [
              {
                mouseButton: MouseBindings.Primary, // Left Click
              },
            ],
          });
        }
        toolGroup?.setToolEnabled(RectangleROITool.toolName);
        toolGroup?.setToolEnabled(ProbeTool.toolName);
        toolGroup?.setToolEnabled(LengthTool.toolName);
        toolGroup?.setToolEnabled(PanTool.toolName);
        toolGroup?.setToolEnabled(WindowLevelTool.toolName);
        toolGroup?.setToolEnabled(MagnifyTool.toolName);
        toolGroup?.setToolEnabled(EllipticalROITool.toolName);
      } else if (mode === AnnotatorConstants.MODES.DRAWING && !isThumbnail) {
        toolGroup?.setToolEnabled(RectangleROITool.toolName);
        toolGroup?.setToolEnabled(ProbeTool.toolName);
        toolGroup?.setToolEnabled(PlanarFreehandROITool.toolName);
        toolGroup?.setToolEnabled(LengthTool.toolName);
        toolGroup?.setToolEnabled(PanTool.toolName);
        toolGroup?.setToolEnabled(WindowLevelTool.toolName);
        toolGroup?.setToolEnabled(MagnifyTool.toolName);
        toolGroup?.setToolEnabled(EllipticalROITool.toolName);
        toolGroup?.setToolEnabled(PlanarFreehandROITool.toolName);
        toolGroup?.setToolEnabled(ArrowAnnotateTool.toolName);
        toolGroup?.setToolEnabled(CircleROITool.toolName);
      } else if (mode === AnnotatorConstants.MODES.MAGNIFYING && !isThumbnail) {
        // In Magnify mode, we allow the user to magnify the image
        toolGroup?.setToolEnabled(RectangleROITool.toolName);
        toolGroup?.setToolEnabled(ProbeTool.toolName);
        toolGroup?.setToolEnabled(PlanarFreehandROITool.toolName);
        toolGroup?.setToolEnabled(LengthTool.toolName);
        toolGroup?.setToolEnabled(PanTool.toolName);
        toolGroup?.setToolEnabled(WindowLevelTool.toolName);
        toolGroup?.setToolActive(MagnifyTool.toolName, {
          bindings: [
            {
              mouseButton: MouseBindings.Primary, // Left Click
            },
          ],
        });
        toolGroup?.setToolEnabled(EllipticalROITool.toolName);
        toolGroup?.setToolEnabled(PlanarFreehandROITool.toolName);
        toolGroup?.setToolEnabled(ArrowAnnotateTool.toolName);
        toolGroup?.setToolEnabled(CircleROITool.toolName);
      } else if (mode === AnnotatorConstants.MODES.VIEWING && !isThumbnail) {
        toolGroup?.setToolPassive(EllipticalROITool.toolName);
        toolGroup?.setToolPassive(RectangleROITool.toolName);
        toolGroup?.setToolPassive(ProbeTool.toolName);
        toolGroup?.setToolPassive(PlanarFreehandROITool.toolName);
        toolGroup?.setToolPassive(MagnifyTool.toolName);
        toolGroup?.setToolPassive(LengthTool.toolName);
        // Only GPU rendering should set brightness/contrast for now
        !getShouldUseCPURendering()
          ? toolGroup?.setToolActive(WindowLevelTool.toolName, {
              bindings: [
                {
                  mouseButton: MouseBindings.Primary, // Left Click
                },
              ],
            })
          : toolGroup?.setToolPassive(WindowLevelTool.toolName);
        toolGroup?.setToolActive(PanTool.toolName, {
          bindings: [
            {
              mouseButton: MouseBindings.Secondary, // Right Click
            },
          ],
        });
        toolGroup?.setToolActive(ZoomToolMouseWheelTool.toolName);
        toolGroup?.setToolEnabled(PlanarFreehandROITool.toolName);
        toolGroup?.setToolEnabled(ArrowAnnotateTool.toolName);
        toolGroup?.setToolEnabled(CircleROITool.toolName);
      } else if (mode === AnnotatorConstants.MODES.VIEWING) {
        toolGroup?.setToolPassive(RectangleROITool.toolName);
        toolGroup?.setToolPassive(EllipticalROITool.toolName);
        toolGroup?.setToolPassive(LengthTool.toolName);
        toolGroup?.setToolPassive(PlanarFreehandROITool.toolName);
        toolGroup?.setToolPassive(WindowLevelTool.toolName);
        toolGroup?.setToolEnabled(PlanarFreehandROITool.toolName);
        toolGroup?.setToolEnabled(ArrowAnnotateTool.toolName);
        toolGroup?.setToolEnabled(CircleROITool.toolName);
      }
    },
    [groupId, annotateAction, isThumbnail],
  );

  // Synch state w/ viewer mode when magnifying is selected
  useEffect(() => {
    cornerstoneLog.log('effect: sync viewer mode', {
      viewerMode,
      isCornerstoneReady,
      viewportCount: renderingEngine
        ? renderingEngine.getViewports().length
        : 0,
    });
    if (!isCornerstoneReady || !renderingEngine.getViewports().length) {
      return;
    }
    changeViewerMode(viewerMode);
  }, [renderingEngine, viewerMode, isCornerstoneReady, changeViewerMode]);

  useEffect(() => {
    if (source === 'additional-image' || !activeImage?.imageId) {
      return;
    }
    trackImageView(
      activeImage,
      source,
      {
        findingType,
        side,
      },
      source === 'thumbnail-previews' ? thumbnailPreviewsPatientId : undefined,
    );
    // Only want to trigger an image view if the image id changes OR patient view toggle is changed OR if the confidence score threshold is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeImage?.imageId,
    showSegmentations,
    confidenceScoreFilter,
    binFilterValue,
  ]);

  const isPAImage = activeImage?.detectedImageType === 'PERIAPICAL';
  // @ts-expect-error TS(2339): Property 'incipientThresholdRange' does not exist ... Remove this comment to see the full error message
  const { incipientThresholdRange } = useIncipient();

  const isPrlEnabled = !!useFeatureFlag(PracticeFeatureFlagKey.PRL_ENABLED)
    ?.value;
  const isAreaOfConcernEnabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.CARIES_SUBCLASSIFICATION_ENABLED,
  )?.value;
  const isRestorativeAdjacentLucenciesEnabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.VIDEA_DENTAL_ASSIST_ENABLED,
  )?.value;
  const isRblEnabled = !!useFeatureFlag(PracticeFeatureFlagKey.RBL_ENABLED)
    ?.value;
  const isCalculusEnabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.CALCULUS_ENABLED,
  )?.value;
  const isAnatomyEnabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.TOOTH_PARTS_SEGMENTATIONS_ENABLED,
  )?.value;
  const { data } = usePreferences();
  const adjustmentVersionHD2 = data?.adjustmentVersionHD2;
  const shouldPreferDisplayOverSharpened = data?.preferDisplayOverSharpened;
  const shouldDisableAdjustmentForPanos = data?.disableAdjustmentForPanos;

  /** This logic was primarily duplicated from FindingViewer */
  const drawFindingsImpl = () => {
    cornerstoneLog.log('drawFindings called', {
      viewportId,
      imageId: activeImage?.imageId,
      imageLoadStatus,
      source,
    });
    const viewportForImage =
      renderingEngine &&
      (renderingEngine.getViewport(viewportId) as
        | Types.IStackViewport
        | undefined);
    if (
      !dicomCanvasRef ||
      !viewportForImage ||
      !renderingEngine ||
      !getEnabledElement(dicomCanvasRef as HTMLDivElement) ||
      !activeImage ||
      !viewportForImage.getCurrentImageId() ||
      source === 'image-browser' ||
      imageLoadStatus !== 'success'
    ) {
      cornerstoneLog.log('drawFindings: skipped (preconditions not met)', {
        hasDicomCanvas: !!dicomCanvasRef,
        hasViewport: !!viewportForImage,
        imageLoadStatus,
        source,
      });
      return;
    }
    try {
      annotation.state
        .getAnnotationManager()
        .removeAnnotations(viewportForImage.getCurrentImageId());
      segmentation.removeSegmentationsFromToolGroup(groupId, undefined, true);
    } catch {
      // Segmentation doesn't exist
    }

    const hasCalcSegmentation = imageWithSegmentations?.teeth?.some(
      (tooth) => !!tooth.calculus?.some((calculus) => !!calculus.outline),
    );

    const hasPrlSegmentation = imageWithSegmentations?.teeth?.some(
      (tooth) => !!tooth.periapicalRadiolucency?.some((prl) => !!prl.outline),
    );
    const hasRalSegmentation = imageWithSegmentations?.teeth?.some(
      (tooth) =>
        !!tooth.restorativeAdjacentLucency?.some((ral) => !!ral.outline),
    );

    const isOnImageViewer =
      source === 'single-image-viewer' ||
      source === 'multi-image-viewer' ||
      source === 'case-presentation' ||
      source === 'case-presentation-download' ||
      source === 'compare-view' ||
      source === 'odontogram-tray';

    const enabledFindingTypes = {
      caries:
        !(!modelsToggles.caries && isOnImageViewer) &&
        !(source === 'compare-visits' && hideFindings) &&
        !(source === 'thumbnail-previews' && findingType !== 'Resto') &&
        !(isOnImageViewer && showSegmentations && imageWithSegmentations) &&
        !(source === 'ins-summary' && !modelsToggles.caries),
      incipients:
        !(!modelsToggles.incipients && isOnImageViewer) &&
        !(source === 'compare-visits' && hideFindings) &&
        !(source === 'thumbnail-previews' && findingType !== 'Resto') &&
        !(isOnImageViewer && showSegmentations && imageWithSegmentations) &&
        !(source === 'ins-summary' && !modelsToggles.caries),
      periapical_radiolucency:
        isPrlEnabled &&
        !(hideFindings && source === 'compare-visits') &&
        !(isOnImageViewer && !modelsToggles.periapical_radiolucency) &&
        source !== 'thumbnail-previews' &&
        !(source === 'ins-summary' && !modelsToggles.periapical_radiolucency) &&
        !(
          isOnImageViewer &&
          data?.displayPRLSegmentations &&
          showSegmentations &&
          imageWithSegmentations &&
          hasPrlSegmentation
        ),
      radiographic_boneloss:
        isRblEnabled &&
        !(hideFindings && source === 'compare-visits') &&
        !(isOnImageViewer && !modelsToggles.radiographic_boneloss) &&
        !(source === 'thumbnail-previews' && findingType !== 'Perio') &&
        !(source === 'ins-summary' && !modelsToggles.radiographic_boneloss),
      user:
        !(hideFindings && source === 'compare-visits') &&
        !(isOnImageViewer && !modelsToggles.user) &&
        source !== 'thumbnail-previews',
      calculus:
        isCalculusEnabled &&
        !(hideFindings && source === 'compare-visits') &&
        !(isOnImageViewer && !modelsToggles.calculus) &&
        (source !== 'thumbnail-previews' || findingType === 'Perio') &&
        !(
          isOnImageViewer &&
          showSegmentations &&
          imageWithSegmentations &&
          hasCalcSegmentation
        ),
      area_of_concern:
        isAreaOfConcernEnabled &&
        (source !== 'thumbnail-previews' || findingType === 'Resto') &&
        !(hideFindings && source === 'compare-visits') &&
        !(isOnImageViewer && !modelsToggles.area_of_concern) &&
        !(isOnImageViewer && showSegmentations && imageWithSegmentations),
      restorative_adjacent_lucency:
        isRestorativeAdjacentLucenciesEnabled &&
        (source !== 'thumbnail-previews' || findingType === 'Resto') &&
        !(hideFindings && source === 'compare-visits') &&
        !(isOnImageViewer && !modelsToggles.restorative_adjacent_lucency) &&
        !(
          isOnImageViewer &&
          data?.displayRALSegmentations &&
          showSegmentations &&
          imageWithSegmentations &&
          hasRalSegmentation
        ),
      tooth_anatomy:
        isAnatomyEnabled &&
        !(!toothPartsToggles.tooth_anatomy && isOnImageViewer) &&
        !(source === 'compare-visits' && hideFindings) &&
        !(isOnImageViewer && showSegmentations && imageWithSegmentations) &&
        source !== 'thumbnail-previews',
    };

    const drawBorderedFindings = () => {
      // We need to add the expected type on each finding in order for the in place utilities to work
      const cariesFindings = activeImage?.models?.caries?.findings?.map(
        (carie) => ({
          ...carie,
          type: CARIES,
        }),
      );
      const { areasOfConcern, caries, incipients } = getCariesSubTypedFindings(
        cariesFindings as any,
      );

      const prlFindings =
        activeImage?.models?.periapical_radiolucency?.findings?.map((prl) => ({
          ...prl,
          type: PERIAPICAL_RADIOLUCENCY,
        }));

      const calculusFindings = activeImage?.models?.calculus?.findings?.map(
        (calculus: any) => ({
          ...calculus,
          type: CALCULUS,
        }),
      );

      const restorativeAdjacentLucencyFindings =
        activeImage?.models?.restorative_adjacent_lucency?.findings?.map(
          (rAJ: any) => ({
            ...rAJ,
            type: RESTORATIVE_ADJACENT_LUCENCY,
          }),
        );

      const userAnnotations = activeImage?.userAnnotations;

      const borderedPathologies = isImageQualityAssessmentOpen
        ? []
        : [
            // If the patient view toggle is on and the image has segmentations, don't show caries
            ...(caries ?? []),
            ...(incipients ?? []),
            ...(prlFindings ?? []),
            ...(calculusFindings ?? []),
            ...(restorativeAdjacentLucencyFindings ?? []),
            ...(areasOfConcern ?? []),
            ...(userAnnotations ?? []),
          ];

      const systemFindingsToDisplay = computeVisibleFindings(
        borderedPathologies,
        enabledFindingTypes,
        modelsToggles,
      );

      for (let j = 0; j < systemFindingsToDisplay?.length; j += 1) {
        const currentFinding = systemFindingsToDisplay[j];
        const color = determineColorForFinding(
          currentFinding,
          AnnotatorConstants.COLORS,
          AnnotatorConstants.VIEWS.DOCTOR,
        );
        const className = determineClassForFinding(
          currentFinding,
          AnnotatorConstants.VIEWS.DOCTOR,
        );

        const isSensitivityRequirementMet =
          (currentFinding.type !== CARIES &&
            currentFinding.type !== AREA_OF_CONCERN) ||
          isWithinSensitivityRange(
            currentFinding,
            activeImage,
            incipientThresholdRange,
          );

        const pointsArray =
          computePointsArrayFromBorderedFinding(currentFinding);

        let isFindingSelected = true;
        if (shouldApplySelectionFiltering() && selectedFinding) {
          isFindingSelected =
            selectedFinding.xMin === currentFinding.xMin &&
            selectedFinding.yMin === currentFinding.yMin &&
            selectedFinding.xMax === currentFinding.xMax &&
            selectedFinding.yMax === currentFinding.yMax;
        }

        if (showToothSpecificFindings) {
          isFindingSelected = !!selectedTeeth?.some(
            (tooth) => tooth === currentFinding?.toothNumber?.toString(),
          );
        }

        const shouldShowFinding =
          isSensitivityRequirementMet &&
          isWithinConfidenceThreshold(
            currentFinding.type,
            currentFinding.confidenceScore,
            currentFinding?.bin,
          ) &&
          isFindingSelected &&
          !hiddenAnnotations.includes(currentFinding.id);

        if (viewportForImage && shouldShowFinding) {
          buildCornerstoneFriendlyAnnotation({
            findingId: currentFinding.id,
            className,
            pointsArray,
            toolName: isEllipseFinding(currentFinding.type)
              ? EllipticalROITool.toolName
              : RectangleROITool.toolName,
            viewportForImage,
            viewportId,
            color: color,
            renderDashedLine: currentFinding.type === CALCULUS,
            dicomCanvasRef: dicomCanvasRef as HTMLDivElement,
          });
        }
      }
    };

    const drawRBLFindings = () => {
      // Don't draw RBL findings when overlay is open
      if (isImageQualityAssessmentOpen) {
        return;
      }

      const rblFindings =
        activeImage?.models?.radiographic_boneloss?.findings?.map((rbl) => ({
          ...rbl,
          type: RADIOGRAPHIC_BONELOSS,
        }));
      if (!enabledFindingTypes.radiographic_boneloss || !rblFindings) {
        return;
      }

      for (let i = 0; i < rblFindings.length; i += 1) {
        const isValidTooth =
          !showToothSpecificFindings ||
          selectedTeeth?.some(
            (tooth) => tooth === rblFindings[i].toothNumber?.toString(),
          );

        rblFindings[i].sides
          .filter(
            (f) => !('validation' in f) || f.userValidation !== 'Disagree',
          )
          .forEach((toothSide: any) => {
            const rblPoints = getAbdCejAndRootPoints(toothSide);
            const selectedPoints = isMatchingRBLPoint(
              rblPoints,
              selectedFinding,
            );
            if (shouldApplySelectionFiltering() && !selectedPoints) {
              return;
            }
            if (!isValidTooth) {
              return;
            }
            let rblLineLength = toothSide.distanceMM;
            if (
              isPAImage &&
              source === 'thumbnail-previews-indicators' &&
              !!rblLineLength
            ) {
              rblLineLength *= 100;
            }

            const rblPointIsHovered = toothSide.points.find(
              (point: { hover: boolean; rblType: string }) =>
                point.hover === true &&
                (point.rblType === 'abd' || point.rblType === 'cej'),
            );
            const color = determineRBLLineColor(
              rblPointIsHovered,
              Number(rblLineLength),
              isPAImage,
              AnnotatorConstants.VIEWS.DOCTOR,
              practiceThreshold,
            );
            const rblClass = determineRBLLineClass(
              rblPointIsHovered,
              Number(rblLineLength),
              isPAImage,
              AnnotatorConstants.VIEWS.DOCTOR,
              practiceThreshold,
            );

            const isRBLLineVisibleOnThumbnail = isModerateOrSevereRblFinding(
              rblLineLength,
              isPAImage,
              practiceThreshold,
            );
            if (isThumbnail && !isRBLLineVisibleOnThumbnail) {
              return;
            }
            if (
              toothSide.points.length > 1 &&
              (shouldApplySelectionFiltering()
                ? selectedFinding && selectedPoints === 'line'
                : true)
            ) {
              const pointsArray: Point2[] = [
                [rblPoints.xCej, rblPoints.yCej],
                [rblPoints.xAbd, rblPoints.yAbd],
              ];
              const findingId = pointsArray.flat().join();
              if (hiddenAnnotations.includes(findingId)) {
                return;
              }
              buildCornerstoneFriendlyAnnotation({
                // No identifier available, use points
                findingId,
                className: `tooth-rbl ${rblClass}`,
                pointsArray,
                toolName: LengthTool.toolName,
                viewportForImage,
                viewportId,
                color,
                dicomCanvasRef: dicomCanvasRef as HTMLDivElement,
                renderDashedLine: false,
              });
            }

            if (isThumbnail || !shouldDisplayRootTips) {
              return;
            }
            const rblRootTipIsHovered = toothSide.points.find(
              (point: any) => point.hover === true && point.rblType === 'root',
            );
            const rootTipColor = rblRootTipIsHovered
              ? AnnotatorConstants.COLORS.rootTipHover
              : AnnotatorConstants.COLORS.purple;

            if (
              rblPoints.xRoot &&
              rblPoints.yRoot &&
              (shouldApplySelectionFiltering()
                ? selectedFinding && selectedPoints === 'root'
                : true)
            ) {
              const findingId = Object.values(rblPoints).join();
              if (hiddenAnnotations.includes(findingId)) {
                return;
              }
              buildCornerstoneFriendlyAnnotation({
                // No identifier available, use points
                findingId,
                className: `tooth-root ${rblClass}`,
                pointsArray: [[rblPoints.xRoot, rblPoints.yRoot]],
                toolName: ProbeTool.toolName,
                viewportForImage,
                viewportId,
                color: rootTipColor,
                dicomCanvasRef: dicomCanvasRef as HTMLDivElement,
              });
            }
          });
      }
    };

    const drawCariesSegmentations = () => {
      if (!imageWithSegmentations) {
        return;
      }

      imageWithSegmentations.teeth?.forEach((tooth) => {
        tooth.caries?.forEach((carie) => {
          const carieUserValidation =
            activeImage?.models?.caries?.findings?.find(
              (finding) => finding.id === carie.id,
            )?.userValidation;

          if (
            shouldApplySelectionFiltering() &&
            selectedFinding &&
            carie.id !== selectedFinding.id
          ) {
            return;
          }
          if (
            !isWithinConfidenceThreshold(
              'caries',
              carie.confidenceScore,
              carie?.bin,
            )
          ) {
            return;
          }
          if (!modelsToggles.incipients && carie.depth === 'into_enamel') {
            return;
          }
          if (!modelsToggles.caries && carie.depth !== 'into_enamel') {
            return;
          }
          const isValidTooth =
            !showToothSpecificFindings ||
            selectedTeeth?.some((x) => x === tooth.toothId);
          if (!isValidTooth) {
            return;
          }
          if (
            carie.findingIdentifier &&
            hiddenAnnotations.includes(carie.findingIdentifier)
          ) {
            return;
          }
          const multipolygon = carie.outline?.coordinates;
          multipolygon?.forEach((polygon) => {
            if (polygon[0]) {
              buildCornerstoneFriendlyAnnotation({
                findingId: carie.findingIdentifier,
                className: determineClassForSegmentation(
                  'caries',
                  carieUserValidation,
                ),
                pointsArray: polygon[0] as Types.Point2[],
                toolName: PlanarFreehandROITool.toolName,
                viewportForImage,
                viewportId,
                color:
                  carie.depth === 'into_enamel'
                    ? 'enamel-only-caries'
                    : 'caries',
                dicomCanvasRef: dicomCanvasRef as HTMLDivElement,
                isLocked: true,
              });
            }
          });
        });
      });
    };

    const drawCalculusSegmentations = () => {
      if (!imageWithSegmentations) {
        return;
      }

      imageWithSegmentations.teeth?.forEach((tooth) => {
        tooth.calculus?.forEach((calculus) => {
          if (
            shouldApplySelectionFiltering() &&
            selectedFinding &&
            !(calculus.id === selectedFinding.id)
          ) {
            return;
          }
          if (
            !isWithinConfidenceThreshold(
              'calculus',
              calculus.confidenceScore,
              calculus?.bin,
            )
          ) {
            return;
          }
          const isValidTooth =
            !showToothSpecificFindings ||
            selectedTeeth?.some((x) => x === tooth.toothId);
          if (!isValidTooth) {
            return;
          }
          const multipolygon = calculus.outline?.coordinates;
          multipolygon?.forEach((polygon) => {
            if (polygon[0]) {
              const findingId = polygon[0].flat().join();
              if (hiddenAnnotations.includes(findingId)) {
                return;
              }

              buildCornerstoneFriendlyAnnotation({
                findingId,
                className: CALCULUS,
                pointsArray: polygon[0] as Types.Point2[],
                toolName: PlanarFreehandROITool.toolName,
                viewportForImage,
                viewportId,
                color: 'calculus',
                dicomCanvasRef: dicomCanvasRef as HTMLDivElement,
                isLocked: true,
              });
            }
          });
        });
      });
    };
    const drawPRLSegmentations = () => {
      imageWithSegmentations?.teeth?.forEach((tooth) => {
        tooth.periapicalRadiolucency?.forEach((prl) => {
          const prlUserValidation =
            activeImage?.models?.periapical_radiolucency?.findings?.find(
              (finding) => finding.id === prl.id,
            )?.userValidation;

          if (
            shouldApplySelectionFiltering() &&
            selectedFinding &&
            !(prl.id === selectedFinding.id)
          ) {
            return;
          }
          if (
            !isWithinConfidenceThreshold(
              'periapical_radiolucency',
              prl.confidenceScore,
              prl?.bin,
            )
          ) {
            return;
          }
          const isValidTooth =
            !showToothSpecificFindings ||
            selectedTeeth?.some((x) => x === tooth.toothId);
          if (!isValidTooth) {
            return;
          }
          const multipolygon = prl.outline?.coordinates;
          multipolygon?.forEach((polygon) => {
            if (polygon[0]) {
              const findingId = polygon[0].flat().join();
              if (hiddenAnnotations.includes(findingId)) {
                return;
              }
              buildCornerstoneFriendlyAnnotation({
                findingId,
                className: determineClassForSegmentation(
                  'periapical_radiolucency',
                  prlUserValidation,
                ),
                pointsArray: polygon[0] as Types.Point2[],
                toolName: PlanarFreehandROITool.toolName,
                viewportForImage,
                viewportId,
                color: 'periapical_radiolucency',
                dicomCanvasRef: dicomCanvasRef as HTMLDivElement,
                isLocked: true,
              });
            }
          });
        });
      });
    };
    const drawRALSegmentations = () => {
      imageWithSegmentations?.teeth?.forEach((tooth) => {
        tooth.restorativeAdjacentLucency?.forEach((ral) => {
          if (
            shouldApplySelectionFiltering() &&
            selectedFinding &&
            !(ral.id === selectedFinding.id)
          ) {
            return;
          }
          if (
            !isWithinConfidenceThreshold(
              'restorative_adjacent_lucency',
              ral.confidenceScore,
              ral?.bin,
            )
          ) {
            return;
          }
          const isValidTooth =
            !showToothSpecificFindings ||
            selectedTeeth?.some((x) => x === tooth.toothId);
          if (!isValidTooth) {
            return;
          }
          const multipolygon = ral.outline?.coordinates;
          multipolygon?.forEach((polygon) => {
            if (polygon[0]) {
              const findingId = polygon[0].flat().join();
              if (hiddenAnnotations.includes(findingId)) {
                return;
              }
              buildCornerstoneFriendlyAnnotation({
                findingId,
                className: RESTORATIVE_ADJACENT_LUCENCY,
                pointsArray: polygon[0] as Types.Point2[],
                toolName: PlanarFreehandROITool.toolName,
                viewportForImage,
                viewportId,
                color: 'restorative_adjacent_lucency',
                dicomCanvasRef: dicomCanvasRef as HTMLDivElement,
                isLocked: true,
              });
            }
          });
        });
      });
    };

    const drawToothAnatomySegmentations = () => {
      if (!imageWithSegmentations) {
        return;
      }
      imageWithSegmentations.teeth?.forEach((tooth) => {
        if (!tooth.enamel && !tooth.pulp) {
          return;
        }
        tooth.enamel?.coordinates.forEach((polygon) => {
          const enamelPolygon = polygon[0];
          if (enamelPolygon) {
            buildCornerstoneFriendlyAnnotation({
              className: 'enamel',
              pointsArray: enamelPolygon as Types.Point2[],
              toolName: PlanarFreehandROITool.toolName,
              viewportForImage,
              viewportId,
              color: 'enamel',
              dicomCanvasRef: dicomCanvasRef as HTMLDivElement,
              isLocked: true,
            });
          }
        });
        tooth.pulp?.coordinates.forEach((polygon) => {
          const pulpPolygon = polygon[0];
          if (pulpPolygon) {
            buildCornerstoneFriendlyAnnotation({
              className: 'pulp',
              pointsArray: pulpPolygon as Types.Point2[],
              toolName: PlanarFreehandROITool.toolName,
              viewportForImage,
              viewportId,
              color: 'pulp',
              dicomCanvasRef: dicomCanvasRef as HTMLDivElement,
              isLocked: true,
            });
          }
        });
      });
    };

    if (
      showSegmentations &&
      isOnImageViewer &&
      (modelsToggles.caries || modelsToggles.incipients)
    ) {
      drawCariesSegmentations();
    }
    if (showSegmentations && isOnImageViewer && modelsToggles.calculus) {
      drawCalculusSegmentations();
    }
    if (
      showSegmentations &&
      isOnImageViewer &&
      toothPartsToggles.tooth_anatomy
    ) {
      drawToothAnatomySegmentations();
    }
    if (
      showSegmentations &&
      isOnImageViewer &&
      modelsToggles.periapical_radiolucency &&
      data?.displayPRLSegmentations
    ) {
      drawPRLSegmentations();
    }
    if (
      showSegmentations &&
      isOnImageViewer &&
      modelsToggles.restorative_adjacent_lucency &&
      data?.displayRALSegmentations
    ) {
      drawRALSegmentations();
    }
    drawBorderedFindings();
    drawRBLFindings();
    triggerAnnotationRenderForViewportIds(renderingEngine, [viewportId]);
  };

  // Stable reference wrapper — avoids cascading re-renders in consumers (e.g. FindingViewer)
  // while always calling the latest implementation.
  const drawFindingsRef = useRef(drawFindingsImpl);
  drawFindingsRef.current = drawFindingsImpl;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- ref wrapper is intentionally stable
  const drawFindings = useCallback(() => drawFindingsRef.current(), []);

  // Every time we hide/unhide findings we need to then redraw findings
  useEffect(() => {
    if (!isCornerstoneReady) {
      return;
    }
    drawFindings();
    // Only need to rerun draw findings if hideFindings/threshold changes
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'drawFindings'. Either include it or remove the dependency array. */
  }, [
    hideFindings,
    confidenceScoreFilter,
    imageWithSegmentations,
    isCornerstoneReady,
    showSegmentations,
    modelsToggles,
    toothPartsToggles,
    hiddenAnnotations,
    shouldDeleteUserAnnotation,
    isImageBrowserExpanded,
    imageLoadStatus,
    binFilterValue,
    selectedTeeth,
    isImageQualityAssessmentOpen,
  ]);

  const loadImageFileIntoCanvas = async (hdVersion = enabledHD) => {
    cornerstoneLog.log('loadImageFileIntoCanvas called', {
      viewportId,
      imageId: activeImage?.imageId,
      hdVersion,
      source,
      isThumbnail,
    });
    setImageLoadStatus('loading');
    const viewportForImage =
      renderingEngine &&
      (renderingEngine.getViewport(viewportId) as
        | Types.IStackViewport
        | undefined);

    if (!activeImage || !viewportForImage) {
      cornerstoneLog.warn(
        'loadImageFileIntoCanvas: missing activeImage or viewport',
        {
          hasActiveImage: !!activeImage,
          hasViewport: !!viewportForImage,
        },
      );
      return;
    }
    let dentalPhotoThumbnail = activeImage.originalPath;
    if (data?.displayDentalThumbnails) {
      if (
        isThumbnail &&
        isImageBrowserExpanded &&
        activeImage.thumbnailMediumPath
      ) {
        dentalPhotoThumbnail = activeImage.thumbnailMediumPath;
      } else if (isThumbnail && activeImage.thumbnailSmallPath) {
        dentalPhotoThumbnail = activeImage.thumbnailSmallPath;
      } else if (activeImage.thumbnailLargePath) {
        dentalPhotoThumbnail = activeImage.thumbnailLargePath;
      }
    }

    const fileTypeToDisplay = computeFileExtension(
      getSelectedImagePath(
        activeImage,
        !!shouldPreferDisplayOverSharpened,
        dentalPhotoThumbnail,
        shouldDisableAdjustmentForPanos,
        hdVersion,
      ),
    );
    const isWado =
      fileTypeToDisplay &&
      IMAGES_WITH_METADATA.includes(fileTypeToDisplay.toLowerCase());

    let imageId = `${isWado ? 'wadouri' : 'web'}:${getSelectedImagePath(
      activeImage,
      !!shouldPreferDisplayOverSharpened,
      dentalPhotoThumbnail,
      shouldDisableAdjustmentForPanos,
      hdVersion,
    )}`;
    imageId += `&updatedAt=${activeImage.updatedAt}`;

    // for now, the tooth selection mode images are thumbnails, but we don't want them to pollute the cache
    const isThumbnailForStack = source !== 'odontogram-tray' && isThumbnail;

    imageId += `${isThumbnailForStack ? '&thumbnail=true' : ''}`;

    if (imageId) {
      metaData.addProvider(
        (type, id) => hardcodedMetaDataProvider(type, id, [imageId]),
        10000,
      );
    }

    setViewportProperties(viewportForImage);
    try {
      const isCached = cache.isImageIdCached(imageId);
      cornerstoneLog.log('loading image stack', {
        imageId: imageId.substring(0, 80) + '...',
        isCached,
        viewportId,
      });
      timerStart(isCached);
      await viewportForImage.setStack([imageId]);
      timerEnd(source, imageId);
      viewportForImage.render();
      setImageLoadStatus('success');
      cornerstoneLog.log('image loaded successfully', { viewportId });
      changeViewerMode(AnnotatorConstants.MODES.VIEWING);
      drawFindings();
    } catch (error) {
      cornerstoneLog.error('image load FAILED', { viewportId, error });
      setImageLoadStatus('error');
    }
  };

  return {
    isCornerstoneReady,
    imageLoadStatus,
    loadImageFileIntoCanvas,
    drawFindings,
  };
};
